import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { Skeleton } from "@/components/ui/skeleton";
import { getRuntimeConfigEntry, setRuntimeConfig } from "@/lib/api";
import {
  emptyEventsConfig,
  newScheduleRow,
  normalizeEventsConfig,
  validateEventsConfig,
  type EventsConfig,
  type LiveOpsListKey,
  type LiveOpsScheduleRow,
} from "@/lib/event-schema";

const EVENTS_KEY = "EC2_EVENTS";

type EditorState =
  | { mode: "new"; listKey: LiveOpsListKey }
  | { mode: "edit"; listKey: LiveOpsListKey; row: LiveOpsScheduleRow; index: number };

const LIST_LABELS: Record<LiveOpsListKey, string> = {
  lobbyBanners: "Lobby banners",
  eventBanners: "Event banners",
  events: "Events",
};

function parseEventsConfig(value: string | undefined): EventsConfig {
  if (!value?.trim()) return emptyEventsConfig();
  const parsed: unknown = JSON.parse(value);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Stored event configuration must be a JSON object.");
  }
  return normalizeEventsConfig(parsed);
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function customJsonToText(value: LiveOpsScheduleRow["customJSON"]): string {
  if (value === null) return "";
  return JSON.stringify(value, null, 2);
}

export function EventsRoute() {
  const queryClient = useQueryClient();
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ listKey: LiveOpsListKey; index: number; id: string } | null>(
    null
  );
  const query = useQuery({
    queryKey: ["runtime-config-entry", EVENTS_KEY],
    queryFn: async () => parseEventsConfig((await getRuntimeConfigEntry(EVENTS_KEY))?.value),
  });
  const config = query.data ?? emptyEventsConfig();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["runtime-config-entry", EVENTS_KEY] });
  const writeConfig = useMutation({
    mutationFn: (next: EventsConfig) => setRuntimeConfig(EVENTS_KEY, JSON.stringify(next)),
    onSuccess: invalidate,
  });

  function toggleActive(listKey: LiveOpsListKey, index: number) {
    const list = [...config[listKey]];
    const row = list[index];
    if (!row) return;
    list[index] = { ...row, active: !row.active };
    writeConfig.mutate({ ...config, [listKey]: list });
  }

  function deleteRow(listKey: LiveOpsListKey, index: number) {
    const list = config[listKey].filter((_, i) => i !== index);
    writeConfig.mutate({ ...config, [listKey]: list }, { onSuccess: () => setConfirmDelete(null) });
  }

  return (
    <AppShell
      title="LiveOps"
      subtitle="Thin EC2_EVENTS schedule: lobby banners, event banners, and events. Prefabs and deals live in Unity Addressables."
    >
      <div className="space-y-6 max-w-6xl">
        <p className="text-xs text-muted-foreground max-w-2xl">
          Each save writes the complete LiveOps document. Rows only need id, contentId, schedule, and optional
          customJSON — Addressable catalogs resolve contentId on the client.
        </p>

        {editor && (
          <RowEditor
            key={`${editor.listKey}-${editor.mode === "edit" ? editor.row.id : "new"}`}
            state={editor}
            config={config}
            onClose={() => setEditor(null)}
            onSaved={(next) => writeConfig.mutate(next, { onSuccess: () => setEditor(null) })}
            saving={writeConfig.isPending}
            saveError={writeConfig.error}
          />
        )}

        {(Object.keys(LIST_LABELS) as LiveOpsListKey[]).map((listKey) => (
          <ScheduleListSection
            key={listKey}
            listKey={listKey}
            rows={config[listKey]}
            loading={query.isLoading}
            error={query.isError ? String(query.error) : null}
            writeError={!editor ? writeConfig.error : null}
            writing={writeConfig.isPending}
            onNew={() => setEditor({ mode: "new", listKey })}
            onEdit={(row, index) => setEditor({ mode: "edit", listKey, row, index })}
            onToggle={(index) => toggleActive(listKey, index)}
            onDelete={(index, id) => setConfirmDelete({ listKey, index, id })}
            editorOpen={editor !== null}
          />
        ))}

        {confirmDelete && (
          <div className="rounded-lg border border-destructive/40 bg-card p-4 space-y-3">
            <p className="text-sm">
              Delete <span className="font-mono">{confirmDelete.id}</span> from {LIST_LABELS[confirmDelete.listKey]}?
              This cannot be undone.
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="destructive"
                onClick={() => deleteRow(confirmDelete.listKey, confirmDelete.index)}
                disabled={writeConfig.isPending}
              >
                {writeConfig.isPending ? "Deleting…" : "Confirm delete"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(null)} disabled={writeConfig.isPending}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function ScheduleListSection({
  listKey,
  rows,
  loading,
  error,
  writeError,
  writing,
  onNew,
  onEdit,
  onToggle,
  onDelete,
  editorOpen,
}: {
  listKey: LiveOpsListKey;
  rows: LiveOpsScheduleRow[];
  loading: boolean;
  error: string | null;
  writeError: unknown;
  writing: boolean;
  onNew: () => void;
  onEdit: (row: LiveOpsScheduleRow, index: number) => void;
  onToggle: (index: number) => void;
  onDelete: (index: number, id: string) => void;
  editorOpen: boolean;
}) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="px-4 py-2 border-b border-border/60 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium">{LIST_LABELS[listKey]}</h3>
          <span className="text-xs text-muted-foreground">
            {rows.length} row{rows.length === 1 ? "" : "s"}
          </span>
        </div>
        <Button size="sm" onClick={onNew} disabled={editorOpen}>
          <Plus className="h-3.5 w-3.5" /> New
        </Button>
      </div>
      {loading ? (
        <div className="p-4">
          <Skeleton className="h-24 w-full" />
        </div>
      ) : error ? (
        <div className="p-4">
          <Alert variant="destructive">
            <AlertDescription>Failed to load: {error}</AlertDescription>
          </Alert>
        </div>
      ) : rows.length === 0 ? (
        <div className="px-4 py-6 text-sm text-muted-foreground text-center">No rows yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">ID</th>
              <th className="px-3 py-2 text-left">contentId</th>
              <th className="px-3 py-2 text-left">Active</th>
              <th className="px-3 py-2 text-left">Date range</th>
              <th className="px-3 py-2 w-20" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.id}-${index}`} className="border-t border-border/50 align-top">
                <td className="px-3 py-2 font-mono text-xs">{row.id}</td>
                <td className="px-3 py-2 font-mono text-xs">{row.contentId}</td>
                <td className="px-3 py-2">
                  <button type="button" onClick={() => onToggle(index)} disabled={writing} title="Toggle active">
                    <Badge variant={row.active ? "success" : "outline"} className="cursor-pointer">
                      {row.active ? "Active" : "Inactive"}
                    </Badge>
                  </button>
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                  {formatDate(row.startDate)}
                  <br />
                  {formatDate(row.endDate)}
                </td>
                <td className="px-3 py-2">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(row, index)} title="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => onDelete(index, row.id)}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {Boolean(writeError) && (
        <div className="p-3">
          <Alert variant="destructive">
            <AlertDescription>{String(writeError)}</AlertDescription>
          </Alert>
        </div>
      )}
    </div>
  );
}

function RowEditor({
  state,
  config,
  onClose,
  onSaved,
  saving,
  saveError,
}: {
  state: EditorState;
  config: EventsConfig;
  onClose: () => void;
  onSaved: (next: EventsConfig) => void;
  saving: boolean;
  saveError: unknown;
}) {
  const isEdit = state.mode === "edit";
  const [row, setRow] = useState<LiveOpsScheduleRow>(isEdit ? state.row : newScheduleRow());
  const [customJsonText, setCustomJsonText] = useState(customJsonToText(isEdit ? state.row.customJSON : null));
  const [customJsonError, setCustomJsonError] = useState<string | null>(null);

  const candidate = useMemo(() => {
    const list = [...config[state.listKey]];
    const nextRow: LiveOpsScheduleRow = {
      ...row,
      id: row.id.trim(),
      contentId: row.contentId.trim(),
    };
    if (isEdit) list[state.index] = nextRow;
    else list.push(nextRow);
    return { ...config, [state.listKey]: list };
  }, [config, state, row, isEdit]);

  const issues = useMemo(() => {
    const fromSchema = validateEventsConfig(candidate);
    if (customJsonError) {
      return [
        ...fromSchema,
        {
          severity: "error" as const,
          path: `${state.listKey}${isEdit ? `[${state.index}]` : `[${config[state.listKey].length}]`}.customJSON`,
          message: customJsonError,
        },
      ];
    }
    return fromSchema;
  }, [candidate, customJsonError, state, config, isEdit]);

  const errors = issues.filter((issue) => issue.severity === "error");
  const issueText = (path: string) =>
    issues
      .filter((issue) => issue.path === path)
      .map((issue) => issue.message)
      .join(" ");

  const basePath = `${state.listKey}${isEdit ? `[${state.index}]` : `[${config[state.listKey].length}]`}`;

  function applyCustomJson(text: string) {
    setCustomJsonText(text);
    const trimmed = text.trim();
    if (!trimmed) {
      setCustomJsonError(null);
      setRow((current) => ({ ...current, customJSON: null }));
      return;
    }
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        setCustomJsonError("customJSON must be a JSON object or empty.");
        return;
      }
      setCustomJsonError(null);
      setRow((current) => ({ ...current, customJSON: parsed as Record<string, unknown> }));
    } catch {
      setCustomJsonError("customJSON is not valid JSON.");
    }
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (errors.length === 0) onSaved(candidate);
  }

  return (
    <form onSubmit={submit} className="rounded-lg border border-border bg-card p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">
          {isEdit ? `Edit ${row.id}` : `New ${LIST_LABELS[state.listKey].slice(0, -1).toLowerCase()}`}
        </h3>
        <Button type="button" size="sm" variant="ghost" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
      </div>

      {issues.length > 0 && (
        <Alert variant={errors.length ? "destructive" : "default"}>
          <AlertDescription className="text-xs">
            <p className="font-medium mb-1">
              {errors.length ? "Fix validation errors before saving:" : "Validation warnings:"}
            </p>
            <ul className="list-disc pl-4">
              {issues.map((issue, index) => (
                <li key={`${issue.path}-${index}`}>
                  <span className="font-mono">{issue.path || "root"}</span>: {issue.message}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Field label="ID" issue={issueText(`${basePath}.id`)}>
          <Input value={row.id} disabled={isEdit} onChange={(e) => setRow({ ...row, id: e.target.value })} />
        </Field>
        <Field label="contentId" issue={issueText(`${basePath}.contentId`)}>
          <Input value={row.contentId} onChange={(e) => setRow({ ...row, contentId: e.target.value })} />
          <p className="text-xs text-muted-foreground">Must match the Addressable Banner/Event ScriptableObject id.</p>
        </Field>
        <Field label="Start date" issue={issueText(`${basePath}.startDate`)}>
          <DateTimePicker value={row.startDate || null} onChange={(value) => setRow({ ...row, startDate: value ?? "" })} />
        </Field>
        <Field label="End date" issue={issueText(`${basePath}.endDate`)}>
          <DateTimePicker value={row.endDate || null} onChange={(value) => setRow({ ...row, endDate: value ?? "" })} />
        </Field>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={row.active} onChange={(e) => setRow({ ...row, active: e.target.checked })} />
        Active
      </label>

      <Field label="customJSON (optional)" issue={issueText(`${basePath}.customJSON`) || customJsonError || undefined}>
        <textarea
          className="flex min-h-28 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
          value={customJsonText}
          onChange={(e) => applyCustomJson(e.target.value)}
          placeholder="{ }"
        />
      </Field>

      <div className="flex gap-2">
        <Button type="submit" disabled={saving || errors.length > 0}>
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
      </div>
      {Boolean(saveError) && (
        <Alert variant="destructive">
          <AlertDescription>{String(saveError)}</AlertDescription>
        </Alert>
      )}
    </form>
  );
}

function Field({ label, issue, children }: { label: string; issue?: string; children: React.ReactNode }) {
  return (
    <label className="space-y-1 text-sm">
      <span>{label}</span>
      {children}
      {issue && <span className="block text-xs text-destructive">{issue}</span>}
    </label>
  );
}
